import { Alert } from 'src/design/atoms/Alert';
import { Box } from 'src/design/atoms/Box';
import { Group } from 'src/design/atoms/Group';
import { Text } from 'src/design/atoms/Text';
import { Title } from 'src/design/atoms/Title';
import { Tooltip } from 'src/design/atoms/Tooltip';
import { NumberInput } from 'src/design/atoms/NumberInput';
import { Table } from 'src/design/molecules/Table';
import { TextInput } from 'src/design/atoms/TextInput';
import { BasicActionIcon } from 'src/design/atoms/BasicActionIcon';
import { IconCheck, IconPlus, IconTrash, IconX } from '@tabler/icons-react';
import React from 'react';
import { ModalButton } from 'src/design/molecules/ModalButton';

export interface IClassificationLevel {
  id?: string;
  name: string;
  rateModifierPercent: number;
}

export interface IClassificationLevelsEditorProps {
  levels: IClassificationLevel[];
  onChange: (levels: IClassificationLevel[]) => void;
  error?: string;
  onAddingChange?: (isAddingNew: boolean) => void;
}

export const ClassificationLevelsEditor: React.FC<
  IClassificationLevelsEditorProps
> = ({ levels, onChange, error, onAddingChange }) => {
  const [isAddingNew, setIsAddingNewState] = React.useState(false);
  const [newLevelForm, setNewLevelForm] = React.useState<{
    name: string;
    rateModifierPercent: number;
  }>({
    name: '',
    rateModifierPercent: 0,
  });

  const setIsAddingNew = (value: boolean) => {
    setIsAddingNewState(value);
    onAddingChange?.(value);
  };

  const handleStartAdd = () => {
    setIsAddingNew(true);
    setNewLevelForm({ name: '', rateModifierPercent: 0 });
  };

  const handleSaveNew = () => {
    if (!newLevelForm.name.trim()) return;

    const newLevel: IClassificationLevel = {
      id: '00000000-0000-0000-0000-000000000000',
      name: newLevelForm.name,
      rateModifierPercent: Number(newLevelForm.rateModifierPercent),
    };

    onChange([...levels, newLevel]);
    setIsAddingNew(false);
    setNewLevelForm({ name: '', rateModifierPercent: 0 });
  };

  const handleCancelAdd = () => {
    setIsAddingNew(false);
    setNewLevelForm({ name: '', rateModifierPercent: 0 });
  };

  const handleDeleteLevel = (index: number) => {
    onChange(levels.filter((_, i) => i !== index));
  };

  const handleUpdateLevel = (
    index: number,
    field: 'name' | 'rateModifierPercent',
    value: string | number,
  ) => {
    const updatedLevels = [...levels];
    updatedLevels[index] = {
      ...updatedLevels[index],
      [field]: field === 'rateModifierPercent' ? Number(value) : value,
    };
    onChange(updatedLevels);
  };

  return (
    <Box>
      <Group justify="space-between" mb="xs">
        <Box>
          <Title order={5}>Levels</Title>
          <Text size="sm">
            Each level defines a rate modifier percentage applied to base pay
          </Text>
        </Box>
      </Group>

      {error && (
        <Alert color="red" title="Validation Error" mb="md">
          {error}
        </Alert>
      )}

      {levels.length > 0 || isAddingNew ? (
        <Table striped highlightOnHover mb="md">
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Level Name</Table.Th>
              <Table.Th style={{ width: 180 }}>Rate Modifier %</Table.Th>
              <Table.Th style={{ width: 80 }}>Actions</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {levels.map((level, index) => (
              <Table.Tr key={index}>
                <Table.Td>
                  <TextInput
                    value={level.name}
                    onChange={(e) =>
                      handleUpdateLevel(index, 'name', e.target.value)
                    }
                    placeholder="Level name"
                    size="sm"
                  />
                </Table.Td>
                <Table.Td>
                  <NumberInput
                    value={level.rateModifierPercent}
                    onChange={(value) =>
                      handleUpdateLevel(
                        index,
                        'rateModifierPercent',
                        value || 0,
                      )
                    }
                    min={0}
                    max={1000}
                    suffix="%"
                    size="sm"
                  />
                </Table.Td>
                <Table.Td>
                  <Tooltip label="Delete">
                    <BasicActionIcon
                      size="sm"
                      variant="subtle"
                      color="red"
                      onClick={() => handleDeleteLevel(index)}
                    >
                      <IconTrash size={16} />
                    </BasicActionIcon>
                  </Tooltip>
                </Table.Td>
              </Table.Tr>
            ))}

            {isAddingNew && (
              <Table.Tr
                style={{
                  backgroundColor: 'var(--mantine-color-blue-0)',
                }}
              >
                <Table.Td>
                  <TextInput
                    value={newLevelForm.name}
                    onChange={(e) =>
                      setNewLevelForm({
                        ...newLevelForm,
                        name: e.target.value,
                      })
                    }
                    placeholder="Level name"
                    size="sm"
                    autoFocus
                  />
                </Table.Td>
                <Table.Td>
                  <NumberInput
                    value={newLevelForm.rateModifierPercent}
                    onChange={(value) =>
                      setNewLevelForm({
                        ...newLevelForm,
                        rateModifierPercent: Number(value) || 0,
                      })
                    }
                    min={0}
                    max={1000}
                    suffix="%"
                    size="sm"
                  />
                </Table.Td>
                <Table.Td>
                  <Group gap="xs">
                    <Tooltip label="Save">
                      <BasicActionIcon
                        size="sm"
                        variant="subtle"
                        color="green"
                        onClick={handleSaveNew}
                        disabled={!newLevelForm.name.trim()}
                      >
                        <IconCheck size={16} />
                      </BasicActionIcon>
                    </Tooltip>
                    <Tooltip label="Cancel">
                      <BasicActionIcon
                        size="sm"
                        variant="subtle"
                        color="gray"
                        onClick={handleCancelAdd}
                      >
                        <IconX size={16} />
                      </BasicActionIcon>
                    </Tooltip>
                  </Group>
                </Table.Td>
              </Table.Tr>
            )}
          </Table.Tbody>
        </Table>
      ) : (
        <Text size="sm" c="dimmed" mb="md">
          No levels added. Levels are optional for a classification.
        </Text>
      )}

      {!isAddingNew && (
        <ModalButton
          onClick={handleStartAdd}
          leftSection={<IconPlus size={16} />}
          variant="light"
        >
          Add Level
        </ModalButton>
      )}
    </Box>
  );
};
